import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/api";
import Swal from "sweetalert2";

import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Paper,
  Grid,
  MenuItem,
  Chip,
  Divider,
  Stack,
  InputAdornment,
  CircularProgress,
} from "@mui/material";

import {
  Edit,
  Delete,
  Download,
  Search,
  Close,
  Save,
  CalendarMonth,
  MenuBook,
  School,
  Assessment,
} from "@mui/icons-material";

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import logo from "./exellentkidslogo.png";

/*
|--------------------------------------------------------------------------
| THEME
|--------------------------------------------------------------------------
*/

const COLORS = {
  black: "#111111",
  darkBlack: "#181818",
  orange: "#F28C28",
  darkOrange: "#D96F0B",
  camel: "#C19A6B",
  lightCamel: "#F4E8D8",
  cream: "#FBF8F3",
  white: "#FFFFFF",
  gray: "#6B7280",
  lightGray: "#F5F5F5",
  border: "#E5E7EB",
  green: "#2E7D32",
  red: "#C62828",
};

/*
|--------------------------------------------------------------------------
| PDF STYLES
|--------------------------------------------------------------------------
*/

const pdfStyles = StyleSheet.create({
  page: {
    padding: 28,
    backgroundColor: "#FBF8F3",
    fontFamily: "Helvetica",
  },

  header: {
    backgroundColor: "#d6d5d4",
    padding: 18,
    borderRadius: 8,
    marginBottom: 18,
    flexDirection: "row",
    alignItems: "center",
  },

  logo: {
    width: 62,
    height: 62,
    marginRight: 15,
  },

  headerContent: {
    flex: 1,
  },

  schoolName: {
    color: "#000000",
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 5,
  },

  schoolInfo: {
    color: "#000000",
    fontSize: 8,
    marginBottom: 3,
  },

  title: {
    color: "#2f1801",
    fontSize: 15,
    fontWeight: "bold",
    marginTop: 7,
  },

  metadata: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 15,
  },

  metadataItem: {
    backgroundColor: "#F4E8D8",
    padding: 8,
    marginRight: 8,
    marginBottom: 8,
    borderRadius: 5,
  },

  metadataLabel: {
    fontSize: 7,
    color: "#6B7280",
    marginBottom: 2,
  },

  metadataValue: {
    fontSize: 9,
    color: "#111111",
    fontWeight: "bold",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ffae45",
    color: "#000000",
    paddingVertical: 8,
    paddingHorizontal: 5,
    fontSize: 7,
    fontWeight: "bold",
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    minHeight: 42,
  },

  tableRowAlt: {
    backgroundColor: "#FFFFFF",
  },

  cell: {
    padding: 6,
    fontSize: 7,
    color: "#222222",
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
  },

  topic: {
    width: "12%",
  },

  subject: {
    width: "10%",
  },

  class: {
    width: "9%",
  },

  period: {
    width: "9%",
  },

  week: {
    width: "6%",
  },

  objectives: {
    width: "17%",
  },

  content: {
    width: "17%",
  },

  method: {
    width: "10%",
  },

  assessment: {
    width: "10%",
  },

  footer: {
    position: "absolute",
    bottom: 18,
    left: 28,
    right: 28,
    borderTopWidth: 1,
    borderTopColor: "#C19A6B",
    paddingTop: 7,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  footerText: {
    fontSize: 8,
    color: "#6B7280",
  },
});

/*
|--------------------------------------------------------------------------
| LESSON PLAN PDF
|--------------------------------------------------------------------------
*/

const LessonPlanPDF = ({
  lessonPlans,
  periodName = "All Periods",
}) => {
  const teachers = [
    ...new Set(
      lessonPlans
        .map((plan) => plan.teacher_name)
        .filter(Boolean)
    ),
  ];

  return (
    <Document>
      <Page
        size="A4"
        orientation="landscape"
        style={pdfStyles.page}
      >
        {/* HEADER */}
        <View style={pdfStyles.header}>
          <Image
            src={logo}
            style={pdfStyles.logo}
          />

          <View style={pdfStyles.headerContent}>
            <Text style={pdfStyles.schoolName}>
             Zion Praise Academy - CRM School System
            </Text>

            <Text style={pdfStyles.schoolInfo}>
               Cowfield 1st Junction Duportroad, Paynesville, Monrovia, Liberia
            </Text>

            <Text style={pdfStyles.schoolInfo}>
              Contact: +231-886909466 / +231-770123025
            </Text>

            <Text style={pdfStyles.schoolInfo}>
              Email: 
            </Text>

            <Text style={pdfStyles.title}>
              WEEKLY LESSON PLAN
            </Text>
          </View>
        </View>

        {/* METADATA */}
        <View style={pdfStyles.metadata}>
          <View style={pdfStyles.metadataItem}>
            <Text style={pdfStyles.metadataLabel}>
              PERIOD
            </Text>

            <Text style={pdfStyles.metadataValue}>
              {periodName}
            </Text>
          </View>

          <View style={pdfStyles.metadataItem}>
            <Text style={pdfStyles.metadataLabel}>
              TOTAL LESSON PLANS
            </Text>

            <Text style={pdfStyles.metadataValue}>
              {lessonPlans.length}
            </Text>
          </View>

          <View style={pdfStyles.metadataItem}>
            <Text style={pdfStyles.metadataLabel}>
              PREPARED BY
            </Text>

            <Text style={pdfStyles.metadataValue}>
              {teachers.length > 0
                ? teachers.join(", ")
                : "Teaching Staff"}
            </Text>
          </View>
        </View>

        {/* TABLE HEADER */}
        <View style={pdfStyles.tableHeader}>
          <Text style={[pdfStyles.cell, pdfStyles.topic]}>
            TOPIC
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.subject]}>
            SUBJECT
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.class]}>
            CLASS
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.period]}>
            PERIOD
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.week]}>
            WEEK
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.objectives]}>
            OBJECTIVES
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.content]}>
            CONTENT
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.method]}>
            METHOD
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.assessment]}>
            ASSESSMENT
          </Text>
        </View>

        {/* TABLE DATA */}
        {lessonPlans.map((plan, index) => (
          <View
            key={plan.id || index}
            style={[
              pdfStyles.tableRow,
              index % 2 === 0
                ? pdfStyles.tableRowAlt
                : {},
            ]}
          >
            <Text style={[pdfStyles.cell, pdfStyles.topic]}>
              {plan.topic || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.subject]}>
              {plan.subject_name || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.class]}>
              {plan.grade_class_name || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.period]}>
              {plan.period_name || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.week]}>
              {plan.week || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.objectives]}>
              {plan.objectives || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.content]}>
              {plan.content || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.method]}>
              {plan.teaching_method || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.assessment]}>
              {plan.assessment || "-"}
            </Text>
          </View>
        ))}

        {/* FOOTER */}
        <View style={pdfStyles.footer}>
          <Text style={pdfStyles.footerText}>
            Official Weekly Lesson Plan Record
          </Text>

          <Text
            style={pdfStyles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

/*
|--------------------------------------------------------------------------
| MAIN COMPONENT
|--------------------------------------------------------------------------
*/

const ViewLessonPlan = () => {
  const [lessonPlans, setLessonPlans] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const accessToken = localStorage.getItem("access");

  /*
  |--------------------------------------------------------------------------
  | FETCH DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchLessonPlans();
    fetchPeriods();
  }, []);

  const fetchLessonPlans = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        "/lesson-plans/",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setLessonPlans(res.data);
    } catch (error) {
      Swal.fire(
        "Error",
        "Unable to load lesson plans.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriods = async () => {
    try {
      const res = await api.get(
        "/api/users/periods/",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setPeriods(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | FILTER LESSON PLANS
  |--------------------------------------------------------------------------
  */

  const filteredPlans = useMemo(() => {
    return lessonPlans.filter((plan) => {
      const matchesPeriod =
        !selectedPeriod ||
        plan.period_name === selectedPeriod;

      const searchText = search.toLowerCase();

      const matchesSearch =
        !search ||
        plan.topic?.toLowerCase().includes(searchText) ||
        plan.subject_name?.toLowerCase().includes(searchText) ||
        plan.grade_class_name?.toLowerCase().includes(searchText) ||
        plan.teacher_name?.toLowerCase().includes(searchText);

      return matchesPeriod && matchesSearch;
    });
  }, [
    lessonPlans,
    selectedPeriod,
    search,
  ]);

  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete Lesson Plan?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      confirmButtonColor: COLORS.red,
      cancelButtonColor: COLORS.black,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(
            `/lesson-plans/${id}/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          Swal.fire(
            "Deleted",
            "Lesson plan deleted successfully.",
            "success"
          );

          fetchLessonPlans();
        } catch (error) {
          Swal.fire(
            "Error",
            "Unable to delete lesson plan.",
            "error"
          );
        }
      }
    });
  };

  /*
  |--------------------------------------------------------------------------
  | EDIT
  |--------------------------------------------------------------------------
  */

  const handleEdit = (plan) => {
    setEditingId(plan.id);

    setEditedData({
      ...plan,
    });
  };

  /*
  |--------------------------------------------------------------------------
  | UPDATE
  |--------------------------------------------------------------------------
  */

  const handleUpdate = async (id) => {
    try {
      await api.put(
        `/lesson-plans/${id}/`,
        editedData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      Swal.fire(
        "Success",
        "Lesson plan updated successfully.",
        "success"
      );

      setEditingId(null);
      fetchLessonPlans();
    } catch (error) {
      Swal.fire(
        "Error",
        "Unable to update lesson plan.",
        "error"
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | DOWNLOAD PDF
  |--------------------------------------------------------------------------
  */

  const downloadPlans = selectedPeriod
    ? filteredPlans
    : lessonPlans;

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: COLORS.cream,
        px: {
          xs: 1.5,
          sm: 3,
          md: 5,
        },
        py: {
          xs: 2,
          md: 4,
        },
      }}
    >
      {/* PAGE HEADER */}
      <Paper
        elevation={0}
        sx={{
          background:
            "linear-gradient(135deg, #111111 0%, #242424 100%)",
          borderRadius: {
            xs: 2,
            md: 3,
          },
          p: {
            xs: 2,
            md: 3,
          },
          mb: 3,
          color: COLORS.white,
          borderBottom: `5px solid ${COLORS.orange}`,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "flex-start",
            md: "center",
          }}
          spacing={2}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: {
                  xs: "1.4rem",
                  sm: "1.8rem",
                  md: "2.1rem",
                },
                letterSpacing: "-0.5px",
              }}
            >
              My Lesson Plans
            </Typography>

            <Typography
              sx={{
                color: "#D1D5DB",
                mt: 0.5,
                fontSize: {
                  xs: "0.82rem",
                  md: "0.95rem",
                },
              }}
            >
              Manage, review, edit and download your weekly teaching plans.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: COLORS.orange,
              px: 2,
              py: 1,
              borderRadius: 2,
            }}
          >
            <MenuBook />

            <Typography
              sx={{
                fontWeight: "bold",
              }}
            >
              {lessonPlans.length} Plans
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* FILTER PANEL */}
      <Paper
        elevation={0}
        sx={{
          p: {
            xs: 1.5,
            md: 2,
          },
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.white,
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
        >
          {/* SEARCH */}
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Search lesson plans"
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* PERIOD */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Period"
              value={selectedPeriod}
              onChange={(e) =>
                setSelectedPeriod(e.target.value)
              }
            >
              <MenuItem value="">
                All Periods
              </MenuItem>

              {periods.map((period) => (
                <MenuItem
                  key={period.id}
                  value={period.name}
                >
                  {period.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* CLEAR */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Close />}
              onClick={() => {
                setSearch("");
                setSelectedPeriod("");
              }}
              sx={{
                height: 40,
                borderColor: COLORS.black,
                color: COLORS.black,
              }}
            >
              Clear
            </Button>
          </Grid>

          {/* DOWNLOAD */}
          <Grid item xs={12} md={2}>
            {downloadPlans.length > 0 ? (
              <PDFDownloadLink
                document={
                  <LessonPlanPDF
                    lessonPlans={downloadPlans}
                    periodName={
                      selectedPeriod ||
                      "All Periods"
                    }
                  />
                }
                fileName={
                  selectedPeriod
                    ? `lesson_plans_${selectedPeriod}.pdf`
                    : "all_lesson_plans.pdf"
                }
                style={{
                  textDecoration: "none",
                  width: "100%",
                }}
              >
                {({ loading: pdfLoading }) => (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Download />}
                    disabled={pdfLoading}
                    sx={{
                      height: 40,
                      backgroundColor: COLORS.orange,
                      color: COLORS.black,
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor: COLORS.darkOrange,
                      },
                    }}
                  >
                    {pdfLoading
                      ? "Preparing..."
                      : "Download PDF"}
                  </Button>
                )}
              </PDFDownloadLink>
            ) : (
              <Button
                fullWidth
                disabled
                variant="contained"
              >
                No Data
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* SUMMARY */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Chip
          icon={<MenuBook />}
          label={`${filteredPlans.length} Showing`}
          sx={{
            backgroundColor: COLORS.black,
            color: COLORS.white,
          }}
        />

        {selectedPeriod && (
          <Chip
            icon={<CalendarMonth />}
            label={selectedPeriod}
            sx={{
              backgroundColor: COLORS.lightCamel,
              color: COLORS.black,
            }}
          />
        )}
      </Box>

      {/* LOADING */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 8,
          }}
        >
          <CircularProgress
            sx={{
              color: COLORS.orange,
            }}
          />
        </Box>
      ) : filteredPlans.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 3,
            border: `1px dashed ${COLORS.camel}`,
          }}
        >
          <MenuBook
            sx={{
              fontSize: 50,
              color: COLORS.camel,
              mb: 1,
            }}
          />

          <Typography
            variant="h6"
            fontWeight="bold"
          >
            No lesson plans found
          </Typography>

          <Typography
            color="text.secondary"
          >
            Try changing your search or period filter.
          </Typography>
        </Paper>
      ) : (
        <Grid
          container
          spacing={2}
        >
          {filteredPlans.map((plan) => (
            <Grid
              item
              xs={12}
              key={plan.id}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  transition: "0.25s ease",
                  "&:hover": {
                    boxShadow:
                      "0 10px 30px rgba(0,0,0,0.08)",
                    transform: {
                      md: "translateY(-2px)",
                    },
                  },
                }}
              >
                {/* CARD HEADER */}
                <Box
                  sx={{
                    backgroundColor: COLORS.black,
                    color: COLORS.white,
                    px: {
                      xs: 1.5,
                      md: 2.5,
                    },
                    py: 1.5,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      fontWeight={900}
                      sx={{
                        fontSize: {
                          xs: "1rem",
                          md: "1.15rem",
                        },
                      }}
                    >
                      {plan.topic}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#D1D5DB",
                        fontSize: "0.8rem",
                      }}
                    >
                      {plan.subject_name} •{" "}
                      {plan.grade_class_name}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Chip
                      label={`Week ${plan.week}`}
                      size="small"
                      sx={{
                        backgroundColor: COLORS.orange,
                        color: COLORS.black,
                        fontWeight: "bold",
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={() =>
                        handleEdit(plan)
                      }
                      sx={{
                        color: COLORS.white,
                        backgroundColor:
                          "rgba(255,255,255,0.1)",
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() =>
                        handleDelete(plan.id)
                      }
                      sx={{
                        color: "#FF8A80",
                        backgroundColor:
                          "rgba(255,255,255,0.1)",
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                {/* EDIT MODE */}
                {editingId === plan.id ? (
                  <Box
                    sx={{
                      p: {
                        xs: 2,
                        md: 3,
                      },
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                    >
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Topic"
                          value={
                            editedData.topic || ""
                          }
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              topic:
                                e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Week"
                          value={
                            editedData.week || ""
                          }
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              week:
                                e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          label="Period"
                          value={
                            editedData.period || ""
                          }
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              period:
                                e.target.value,
                            })
                          }
                        >
                          {periods.map(
                            (period) => (
                              <MenuItem
                                key={period.id}
                                value={period.id}
                              >
                                {period.name}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                      </Grid>

                      {[
                        {
                          label: "Objectives",
                          field: "objectives",
                        },
                        {
                          label: "Content",
                          field: "content",
                        },
                        {
                          label: "Teaching Method",
                          field:
                            "teaching_method",
                        },
                        {
                          label: "Assessment",
                          field: "assessment",
                        },
                      ].map((item) => (
                        <Grid
                          item
                          xs={12}
                          key={item.field}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={item.label}
                            value={
                              editedData[
                                item.field
                              ] || ""
                            }
                            onChange={(e) =>
                              setEditedData({
                                ...editedData,
                                [item.field]:
                                  e.target.value,
                              })
                            }
                          />
                        </Grid>
                      ))}

                      <Grid item xs={12}>
                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          spacing={1}
                        >
                          <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={() =>
                              handleUpdate(
                                plan.id
                              )
                            }
                            sx={{
                              backgroundColor:
                                COLORS.orange,
                              color:
                                COLORS.black,
                              fontWeight:
                                "bold",
                            }}
                          >
                            Save Changes
                          </Button>

                          <Button
                            variant="outlined"
                            onClick={() =>
                              setEditingId(null)
                            }
                            sx={{
                              color:
                                COLORS.black,
                              borderColor:
                                COLORS.black,
                            }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  /* VIEW MODE */
                  <Box
                    sx={{
                      p: {
                        xs: 1.5,
                        md: 2.5,
                      },
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                    >
                      <Grid item xs={12} sm={6} md={3}>
                        <InfoBlock
                          icon={<School />}
                          label="Class"
                          value={
                            plan.grade_class_name
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <InfoBlock
                          icon={<CalendarMonth />}
                          label="Period"
                          value={
                            plan.period_name
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <InfoBlock
                          icon={<MenuBook />}
                          label="Subject"
                          value={
                            plan.subject_name
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <InfoBlock
                          icon={<Assessment />}
                          label="Week"
                          value={
                            `Week ${plan.week}`
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            my: 0.5,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ContentBlock
                          title="Objectives"
                          content={
                            plan.objectives
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ContentBlock
                          title="Lesson Content"
                          content={
                            plan.content
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ContentBlock
                          title="Teaching Method"
                          content={
                            plan.teaching_method
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ContentBlock
                          title="Assessment"
                          content={
                            plan.assessment
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| INFO BLOCK
|--------------------------------------------------------------------------
*/

const InfoBlock = ({
  icon,
  label,
  value,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        backgroundColor: COLORS.lightCamel,
        p: 1.2,
        borderRadius: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          color: COLORS.darkOrange,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "0.7rem",
            color: COLORS.gray,
            fontWeight: "bold",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontWeight: "bold",
            color: COLORS.black,
            fontSize: "0.9rem",
          }}
        >
          {value || "-"}
        </Typography>
      </Box>
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| CONTENT BLOCK
|--------------------------------------------------------------------------
*/

const ContentBlock = ({
  title,
  content,
}) => {
  return (
    <Box
      sx={{
        borderLeft: `4px solid ${COLORS.orange}`,
        backgroundColor: COLORS.cream,
        p: 1.5,
        borderRadius: "0 8px 8px 0",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          color: COLORS.black,
          mb: 0.5,
          fontSize: "0.9rem",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: COLORS.gray,
          fontSize: "0.88rem",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
        }}
      >
        {content || "No information provided."}
      </Typography>
    </Box>
  );
};

export default ViewLessonPlan;

